import cn from 'classnames';
import React, {forwardRef} from 'react';

import AemEditableText from '@/components/Aem/AemEditableText/AemEditableText';
import BackButton from '@/components/BackButton/BackButton';
import {AppButton, ButtonRaw} from '@/components/Button';
import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import {ChevronRightIcon} from '@/components/Icons';
import {StickyPageLayout} from '@/components/Layout';
import useIsDesktopLayout from '@/hooks/layout/useIsDesktopLayout';
import routerService from '@/services/route/routerService';
import {twx} from '@/utils/tailwindUtils';

const DevicesSettingsSectionNew = () => null;

const Container = forwardRef(
    ({children, className, title, onClick, pageName, itemName, isContentContainerFocusable, ...rest}, ref) => {
        const isDesktop = useIsDesktopLayout();

        if (isDesktop) {
            return (
                <div ref={ref} className={cn(className, 'ml:px-8 ml:pt-8')} {...rest}>
                    <BackButton />
                    {children}
                </div>
            );
        }

        return (
            <StickyPageLayout
                ref={ref}
                className='items-center'
                contentClassName='flex flex-1 flex-col items-center'
                headerOptions={{
                    onBack: routerService.goBack,
                    title,
                }}
                footer={
                    <AppButton className='m-auto' primary light onClick={onClick}>
                        <AemEditableText pageName={pageName} itemName={itemName} />
                    </AppButton>
                }
                isContentContainerFocusable={isContentContainerFocusable}
                {...rest}
            >
                {children}
            </StickyPageLayout>
        );
    }
);

const Group = ({children, className}) => {
    return <div className={twx('px-4 md:px-8', className)}>{children}</div>;
};

const GroupTitle = ({text, className}) => {
    return (
        text && (
            <h2
                className={twx(
                    'mb-4 text-20 leading-12 tracking-02 md:text-20 ml:mb-6 ml:text-24 ml:leading-15 ml:tracking-normal',
                    className
                )}
            >
                {text}
            </h2>
        )
    );
};

const GroupButton = ({
    className,
    titleClassName,
    title,
    subTitle,
    icon,
    as: Component = ButtonRaw,
    children,
    hideArrow,
    onClick,
    isLast,
    isDisabled,
    onDisabledClick,
}) => {
    return (
        <>
            <Component
                className={twx(
                    'group flex w-full flex-col justify-between px-4 pt-4 ml:rounded-xl ml:bg-primary-dark-5',
                    className
                )}
                onClick={isDisabled ? onDisabledClick : onClick}
            >
                <div className='flex w-full items-center justify-between gap-2 border-b border-primary-dark-15 pb-4 group-last:border-0 ml:border-0'>
                    <RemoteIconV2 icon={icon} className={twx('h-12 w-12 shrink-0', isDisabled && 'opacity-60')} />
                    <div
                        className={twx(
                            'mr-auto flex flex-col pr-1 text-14 leading-143',
                            isDisabled && 'text-primary-dark-85'
                        )}
                    >
                        {title && <span className={twx('tracking-04', titleClassName)}>{title}</span>}
                        {subTitle && (
                            <span className={twx('font-bold tracking-02', isDisabled && 'opacity-60')}>{subTitle}</span>
                        )}
                    </div>
                    {children}
                    {!hideArrow && <ChevronRightIcon className='shrink-0' />}
                </div>
            </Component>
        </>
    );
};

DevicesSettingsSectionNew.Container = Container;
DevicesSettingsSectionNew.Group = Group;
DevicesSettingsSectionNew.Title = GroupTitle;
DevicesSettingsSectionNew.Button = GroupButton;

export default DevicesSettingsSectionNew;
